import { Helmet } from 'react-helmet-async';
import { BASE_URL, DEFAULT_IMAGE, absoluteUrl } from '@/lib/seo';

/**
 * SEOHead – per-page SEO component.
 * Place inside any page component (inside <Layout>).
 *
 * @param {string}  title       – page-specific title (appended with " | DeinServerHost"),
 *                                keep it ≤ 43 chars so the full title stays ≤ 60
 * @param {string}  description – meta description (recommended 120–160 chars)
 * @param {string}  canonical   – path relative to root, e.g. "/vps/kvm-ryzen"
 * @param {string}  ogImage     – absolute URL or path to OG image
 * @param {object}  schema      – Schema.org JSON-LD object (or array)
 * @param {boolean} noindex     – set true for legal / utility pages
 */
export function SEOHead({
  title,
  description,
  canonical,
  ogImage = DEFAULT_IMAGE,
  schema,
  noindex = false,
}) {
  const fullTitle = title
    ? `${title} | DeinServerHost`
    : 'DeinServerHost – Rent Prepaid Server | Gameserver, VPS, Dedicated';

  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : null;
  const ogImageUrl = absoluteUrl(ogImage);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow" />
      }
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {canonicalUrl && <link rel="alternate" hrefLang="en" href={canonicalUrl} />}
      {canonicalUrl && <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:site_name" content="DeinServerHost" />

      {/* Twitter Card – "summary" because the default image is square (500×500) */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content="@Deinserverhost" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImageUrl} />

      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}
